import { NavButton } from "@src/components/navButton/NavButton";
import "./numSecu.scss";
import {
  Form,
  InputMask,
  InputText,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormRow,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { Stepbar } from "@src/components/stepbar/Stepbar";
import { MicroStep, StepProps } from "@src/Steps/step/Steps";
import { getMonth, getYear } from "date-fns";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";

export type NumSecuData =
  | undefined
  | { numSecu: string; cityOfBirth: string; countryOfBirth: string };
type NumSecuProps = StepProps<NumSecuData>;

function cleanMaskedInput(val: unknown) {
  if (typeof val !== "string") return undefined;
  return val.replace(/\s/g, "");
}

const NUM_SECU_BY_CIVILITY = {
  Monsieur: "1",
  Madame: "2",
};

const schema = zod
  .object({
    numSecu: zod.preprocess(
      cleanMaskedInput,
      zod
        .string({ required_error: "Champ requis" })
        .length(15, "Numéro de sécurité sociale invalide")
        .regex(/^[1-2]/, { message: "Numéro de sécurité sociale invalide" })
    ),
    yearOfBirth: zod.string(),
    monthOfBirth: zod.string(),
    civility: zod.string(),
    cityOfBirth: zod
      .string({ required_error: "Champ requis" })
      .min(1, "Champ requis"),
    countryOfBirth: zod
      .string({ required_error: "Champ requis" })
      .min(1, "Champ requis"),
  })
  .superRefine((data, ctx) => {
    if (!data.numSecu || data.numSecu.length < 3) return;
    if (
      data.numSecu.substring(0, 1) !==
      NUM_SECU_BY_CIVILITY[data.civility as "Monsieur" | "Madame"]
    ) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ["numSecu"],
        message: "La civilité ne correspond pas",
      });
    }
    if (data.numSecu.substring(1, 3) !== data.yearOfBirth) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ["numSecu"],
        message: "L'année de naissance ne correspond pas",
      });
    }
    if (data.numSecu.substring(3, 5) !== data.monthOfBirth) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ["numSecu"],
        message: "Le mois de naissance ne correspond pas",
      });
    }
    const departementCode = data.numSecu.substring(5, 7);
    const validDepartments = [
      ...Array.from({ length: 99 }, (_, i) => String(i + 1).padStart(2, "0")),
      "2A",
      "2B",
    ];
    if (!validDepartments.includes(departementCode)) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ["numSecu"],
        message: "Département de naissance invalide",
      });
    }
    const communeCode = data.numSecu.substring(7, 10);
    if (communeCode.search(/^\d{3}$/) === -1 || communeCode === "000") {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ["numSecu"],
        message: "Commune de naissance invalide",
      });
    }

    const numSecuCle = data.numSecu.substring(13, 15);
    if (
      numSecuCle &&
      97 - (parseInt(data.numSecu.substring(0, 13), 10) % 97) !==
        parseInt(numSecuCle, 10)
    ) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ["numSecu"],
        message: "Clé invalide",
      });
    }
  });
type FormValues = zod.infer<typeof schema>;

function getLast2DigitsOfDateYear(date: Date | undefined) {
  if (!date) return undefined;
  return String(getYear(date)).slice(-2);
}

function getLast2DigitsOfDateMonth(date: Date | undefined) {
  if (!date) return undefined;
  return String(getMonth(date) + 1).padStart(2, "0");
}

export function NumSecu({
  changeStep,
  data,
  projetSouscription,
  setData,
}: NumSecuProps) {
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
    defaultValues: {
      yearOfBirth: getLast2DigitsOfDateYear(
        projetSouscription?.assure.birthDate
      ),
      monthOfBirth: getLast2DigitsOfDateMonth(
        projetSouscription?.assure.birthDate
      ),
      civility: projetSouscription?.assure.civility,
      ...data,
    },
    mode: "onChange",
  });

  const handleSubmit = (submittedData: FormValues) => {
    setData({
      numSecu: cleanMaskedInput(submittedData.numSecu) as string,
      cityOfBirth: submittedData.cityOfBirth,
      countryOfBirth: submittedData.countryOfBirth,
    });
    changeStep("POLITIQUEMENT_EXPOSEE", "forward");
  };

  return (
    <div className="num-secu-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid step__grid--padding-bottom">
          <Stepbar activeMicroStep={MicroStep.PersonalInformation} />
          <NavButton
            onClick={() => {
              changeStep("AVANT_DE_POURSUIVRE", "backward");
            }}
          >
            Précédent
          </NavButton>
          <Form
            className="num-secu-step__form"
            formConfig={formConfig}
            onSubmit={handleSubmit}
          >
            <FormGrid>
              <FormTitle>
                <h1>
                  Quel est votre{" "}
                  <Emphasis>numéro de sécurité sociale ? *</Emphasis>
                </h1>
              </FormTitle>
              <FormRow>
                <FormField>
                  <InputMask<FormValues>
                    variant="large"
                    name="numSecu"
                    placeholder="15 chiffres"
                    mask="9 99 99 99 999 999 99"
                    required
                  />
                </FormField>
              </FormRow>
              <FormTitle className="num-secu-step__title">
                <h1>
                  Quels sont votre{" "}
                  <Emphasis>ville et pays de naissance ?</Emphasis>
                </h1>
              </FormTitle>
              <FormRow>
                <FormField>
                  <InputText
                    name="cityOfBirth"
                    required
                    label="Ville de naissance"
                    placeholder="Paris"
                  />
                </FormField>
                <FormField>
                  <InputText
                    name="countryOfBirth"
                    required
                    label="Pays de naissance"
                    placeholder="France"
                  />
                </FormField>
              </FormRow>
              <FormAction>
                <Button
                  type="submit"
                  disabled={
                    !formConfig.formState.isValid ||
                    formConfig.formState.isSubmitting ||
                    formConfig.formState.isSubmitSuccessful
                  }
                >
                  Suivant
                </Button>
              </FormAction>
            </FormGrid>
          </Form>
        </div>
      </div>
    </div>
  );
}
